import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

const defaultLabelKey = 'label';
const defaultValueKey = 'value';

const isSelectionReachedLimit = (limit, selectedValuesLength) =>
  typeof limit === 'number' && limit >= 0 && selectedValuesLength >= limit;

const MultiSelectList = ({
  options,
  onClick,
  disabled,
  labelKey,
  valueKey,
}) => {
  return (
    <ul className="msts__list">
      {options.map((option) => {
        const itemDisabled = disabled || option.disabled;
        const className = classNames(
          'msts__list-item',
          itemDisabled && 'msts__list-item_disabled',
          option.highlighted && 'msts__list-item_highlighted',
        );

        return (
          <li
            key={String(option[valueKey])}
            className={className}
            onClick={() => {
              if (!itemDisabled) {
                onClick(option[valueKey]);
              }
            }}
          >
            {option[labelKey]}
          </li>
        );
      })}
    </ul>
  );
};

const FilterInput = ({
  value,
  onChange,
  placeholder,
  clearable,
  clearFilterText,
  disabled,
}) => {
  return (
    <div className="msts__filter">
      <input
        className="msts__filter-input"
        onChange={(event) => onChange(event.target.value)}
        type="text"
        disabled={disabled}
        placeholder={placeholder}
        value={value}
      />
      {clearable && value && !disabled ? (
        <span
          className="msts__filter-clear"
          onClick={() => onChange('')}
          title={clearFilterText}
        />
      ) : null}
    </div>
  );
};

const MultiselectTwoSides = ({
  availableFooter = null,
  availableHeader = null,
  className = null,
  clearFilterText = 'Clear',
  clearable = true,
  deselectAllText = 'Deselect all',
  disabled = false,
  highlight = [],
  labelKey = defaultLabelKey,
  limit,
  onChange = () => {},
  options = [],
  placeholder = '',
  searchable = false,
  selectAllText = 'Select all',
  selectedFooter = null,
  selectedHeader = null,
  showControls = false,
  value = [],
  valueKey = defaultValueKey,
}) => {
  const [filterAvailable, setFilterAvailable] = useState('');
  const [filterSelected, setFilterSelected] = useState('');

  const selectedValues = useMemo(() => new Set(value), [value]);

  const availableOptions = useMemo(() => {
    const limitReached = isSelectionReachedLimit(limit, value.length);

    const transformed = options
      .filter((option) => !selectedValues.has(option[valueKey]))
      .map((option) => ({
        ...option,
        disabled: Boolean(option.disabled || limitReached),
        highlighted: highlight.includes(option[valueKey]),
      }));

    if (!searchable || !filterAvailable) {
      return transformed;
    }

    const loweredFilter = filterAvailable.toLowerCase();
    return transformed.filter((option) =>
      String(option[labelKey]).toLowerCase().includes(loweredFilter),
    );
  }, [
    filterAvailable,
    highlight,
    labelKey,
    limit,
    options,
    searchable,
    selectedValues,
    value.length,
    valueKey,
  ]);

  const selectedOptions = useMemo(() => {
    const transformed = options.filter((option) =>
      selectedValues.has(option[valueKey]),
    );

    if (!searchable || !filterSelected) {
      return transformed;
    }

    const loweredFilter = filterSelected.toLowerCase();
    return transformed.filter((option) =>
      String(option[labelKey]).toLowerCase().includes(loweredFilter),
    );
  }, [filterSelected, labelKey, options, searchable, selectedValues, valueKey]);

  const handleSelect = (optionValue) => {
    if (disabled || selectedValues.has(optionValue)) {
      return;
    }

    if (isSelectionReachedLimit(limit, value.length)) {
      return;
    }

    onChange(value.concat(optionValue));
  };

  const handleDeselect = (optionValue) => {
    if (disabled) {
      return;
    }

    onChange(value.filter((item) => item !== optionValue));
  };

  const handleSelectAll = () => {
    if (disabled) {
      return;
    }

    const additionalValues = options
      .filter(
        (option) => !option.disabled && !selectedValues.has(option[valueKey]),
      )
      .map((option) => option[valueKey]);

    const next = value.concat(additionalValues);
    const limited =
      typeof limit === 'number' && limit >= 0 ? next.slice(0, limit) : next;
    onChange(limited.sort());
  };

  const handleDeselectAll = () => {
    if (disabled) {
      return;
    }

    const lockedSelected = options
      .filter(
        (option) => option.disabled && selectedValues.has(option[valueKey]),
      )
      .map((option) => option[valueKey]);

    onChange(lockedSelected);
  };

  return (
    <div className={classNames('msts', disabled && 'msts_disabled', className)}>
      {availableHeader || selectedHeader ? (
        <div className="msts__heading">
          <div className="msts__side msts__side_available">
            {availableHeader}
          </div>
          <div className="msts__side msts__side_selected">{selectedHeader}</div>
        </div>
      ) : null}

      {searchable ? (
        <div className="msts__subheading">
          <div className="msts__side msts__side_filter">
            <FilterInput
              value={filterAvailable}
              onChange={setFilterAvailable}
              placeholder={placeholder}
              clearable={clearable}
              clearFilterText={clearFilterText}
              disabled={disabled}
            />
          </div>
          <div className="msts__side msts__side_filter">
            <FilterInput
              value={filterSelected}
              onChange={setFilterSelected}
              placeholder={placeholder}
              clearable={clearable}
              clearFilterText={clearFilterText}
              disabled={disabled}
            />
          </div>
        </div>
      ) : null}

      <div className="msts__body">
        <div className="msts__side msts__side_available">
          <MultiSelectList
            options={availableOptions}
            onClick={handleSelect}
            disabled={disabled}
            labelKey={labelKey}
            valueKey={valueKey}
          />
        </div>

        {showControls ? (
          <div className="msts__side msts__side_controls">
            <button
              className="msts__control msts__control_select-all"
              title={selectAllText}
              type="button"
              disabled={
                value.length === options.length ||
                isSelectionReachedLimit(limit, value.length) ||
                disabled
              }
              onClick={handleSelectAll}
            />
            <button
              className="msts__control msts__control_deselect-all"
              title={deselectAllText}
              type="button"
              disabled={!value.length || disabled}
              onClick={handleDeselectAll}
            />
          </div>
        ) : null}

        <div className="msts__side msts__side_selected">
          <MultiSelectList
            options={selectedOptions}
            onClick={handleDeselect}
            disabled={disabled}
            labelKey={labelKey}
            valueKey={valueKey}
          />
        </div>
      </div>

      {availableFooter || selectedFooter ? (
        <div className="msts__footer">
          <div className="msts__side msts__side_available">
            {availableFooter}
          </div>
          <div className="msts__side msts__side_selected">{selectedFooter}</div>
        </div>
      ) : null}
    </div>
  );
};

export default MultiselectTwoSides;
